import { Component } from '@theme/component';

class LevayoAccountMenu extends Component {
  requiredRefs = ['trigger', 'panel'];
  #controller;

  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }

  #bind() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    if (!this.refs.panel.showPopover) return;
    this.refs.trigger.setAttribute('role', 'button');
    this.refs.trigger.setAttribute('aria-expanded', String(this.#isOpen()));
    this.refs.panel.addEventListener('toggle', (event) => this.refs.trigger.setAttribute('aria-expanded', String(event.newState === 'open')), { signal });
    this.addEventListener('keydown', (event) => this.#key(event), { signal });
    this.addEventListener('focusout', (event) => { if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(); }, { signal });
    this.refs.panel.addEventListener('click', (event) => { if (event.target.closest('[data-account-link]')) this.close(); }, { signal });
    document.addEventListener('pointerdown', (event) => { if (!this.contains(event.target)) this.close(); }, { signal });
    document.addEventListener('theme-drawer:open', () => this.close(), { signal });
    document.addEventListener('dialog:open', () => this.close(), { signal });
    document.addEventListener('scroll', () => { if (this.#isOpen()) this.#position(); }, { capture: true, passive: true, signal });
    window.addEventListener('resize', () => { if (this.#isOpen()) this.#position(); }, { passive: true, signal });
  }

  disconnectedCallback() { this.close(); this.#controller?.abort(); super.disconnectedCallback(); }
  #isOpen() { return !this.refs.panel.hidden && this.refs.panel.matches(':popover-open'); }

  toggle(event) {
    if (event?.metaKey || event?.ctrlKey || event?.shiftKey || event?.altKey) return;
    if (!this.refs.panel.showPopover) return;
    event?.preventDefault();
    if (this.#isOpen()) this.close(); else this.open();
  }

  open() {
    if (!this.#position()) return;
    this.refs.panel.hidden = false;
    this.refs.panel.showPopover();
    this.#position();
    this.refs.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (this.refs.panel && this.#isOpen()) this.refs.panel.hidePopover();
    this.refs.trigger?.setAttribute('aria-expanded', 'false');
  }

  closeAndFocus() { this.close(); this.refs.trigger.focus({ preventScroll: true }); }

  #position() {
    const rect = this.refs.trigger.getBoundingClientRect();
    if (!rect.width || rect.bottom < 0 || rect.top > innerHeight) { this.close(); return false; }
    const width = Math.min(340, innerWidth - 24);
    const left = Math.max(12, Math.min(rect.right - width, innerWidth - width - 12));
    const below = innerHeight - rect.bottom - 22;
    const above = rect.top - 22;
    const placeAbove = below < 180 && above > below;
    const height = Math.max(72, Math.min(placeAbove ? above : below, 560));
    const panel = this.refs.panel;
    const contentHeight = Math.min(height, panel.scrollHeight ? panel.scrollHeight + 2 : height);
    panel.style.setProperty('--lvaccount-width', `${width}px`);
    panel.style.setProperty('--lvaccount-left', `${left}px`);
    panel.style.setProperty('--lvaccount-top', `${placeAbove ? rect.top - contentHeight - 10 : rect.bottom + 10}px`);
    panel.style.setProperty('--lvaccount-height', `${height}px`);
    return true;
  }

  #key(event) {
    if (event.key === 'Escape' && this.#isOpen()) { event.preventDefault(); event.stopPropagation(); this.closeAndFocus(); return; }
    if (event.target === this.refs.trigger && [' ', 'Enter'].includes(event.key)) { this.toggle(event); return; }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    if (!this.#isOpen()) this.open();
    const links = Array.from(this.refs.panel.querySelectorAll('[data-account-link]'));
    const index = links.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
    event.preventDefault();
    if (next < 0) this.refs.trigger.focus({ preventScroll: true });
    else {
      const link = links[Math.min(next, links.length - 1)];
      link?.focus({ preventScroll: true });
      link?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }
}

if (!customElements.get('levayo-account-menu')) customElements.define('levayo-account-menu', LevayoAccountMenu);
